/**
 * Review queue — the recruiter's determination dashboard (owner workflow
 * 2026-08-03).
 *
 * One place holding everything a human must decide on: escalated questions
 * the agent held for a person, and leads whose screening is complete and
 * parked awaiting an eligibility determination. The recruiter reads the
 * context, writes up what to do, picks the outcome, and submits — the agent
 * then composes the friendly SMS and AUTO-SENDS it (the recruiter authored
 * the substance), applying the outcome after the send:
 *
 *   Move forward        → agent relays next steps (can carry the booking link)
 *   Pivot to a study    → conversation swaps to the target study
 *   Not eligible now    → lifecycle moves to Not eligible (syncs to CRIO),
 *                         friendly "future studies — apply again" message
 *   Answer only         → the recruiter's answer is relayed, nothing else
 *
 * Master–detail like the Board (density bar): list left, context + composer
 * right. EscalationContext is reused verbatim for the "what caused this"
 * thread window.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ClipboardCheck, HelpCircle, Loader2, CheckCircle2, XCircle,
  ChevronRight, ExternalLink, User, Bot,
} from 'lucide-react';
import api from '../../../services/api';
import { RowLink } from '../../components/shared';
import RecruiterBadge from '../../components/RecruiterBadge';
import { type StudyOption } from '../../types';
import {
  ESCALATION_KIND_LABEL as KIND_LABEL,
  escalationGuide,
  EscalationBreakdown,
} from '../../components/escalationDisplay';
import EscalationContext from '../../components/agent/EscalationContext';
import DeterminationComposer, { OUTCOME_LABEL } from '../../components/agent/DeterminationComposer';
import ScreenAnswersPanel, { useConversationScreen } from '../../components/agent/ScreenAnswersPanel';
import { ago, btnGhost } from '../../components/agent/shared';

export const QUESTION_KINDS = ['unknown_answer', 'bot_question', 'human_requested', 'call_request', 'hostile_or_distress'];
export const ELIG_KINDS = ['handoff_review', 'dormant_handback'];
/** Owner rule 2026-08-03: silent mid-screening flags surface here too, so a
 *  recruiter can end a doomed screen early. One item per conversation. */
export const SCREEN_KINDS = ['prescreen_flag'];

export interface QueueEscalation {
  id: string;
  conversation_id: string;
  contact_id?: string | null;
  contact_name?: string | null;
  owner_user_id?: string | null;
  kind: string;
  status: string;
  claimed_by: string | null;
  payload: any;
  created_at: string;
}

export interface QueueReview {
  conversation_id: string;
  contact_id: string;
  contact_name: string | null;
  owner_user_id?: string | null;
  study_id: string | null;
  study_title: string | null;
  session_id: string | null;
  last_agent_message_at: string | null;
  last_inbound_at: string | null;
  created_at: string;
}

export interface QueueDetermination {
  id: string;
  conversation_id: string;
  contact_id?: string | null;
  contact_name?: string | null;
  kind: string;
  outcome: string;
  summary: string;
  status: string;
  error: string | null;
  created_at: string;
  delivered_at: string | null;
}

/** One work item: an awaiting-review lead (with its handoff escalation when
 *  one is open) or a standalone escalated question. */
export interface QueueItem {
  key: string;
  group: 'eligibility' | 'question' | 'screening';
  conversation_id: string;
  contact_id: string | null;
  contact_name: string | null;
  owner_user_id: string | null;
  study_id: string | null;
  study_title: string | null;
  escalation: QueueEscalation | null;
  /** Screening items only: every open flag on the conversation. */
  flags?: QueueEscalation[];
  created_at: string;
  claimed: boolean;
}

export const DET_STATUS_PILL: Record<string, string> = {
  submitted: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  delivering: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  canceled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

/**
 * Group raw escalations + awaiting-review conversations into the queue's work
 * items: one row per conversation, eligibility reviews first (a conversation's
 * open handoff escalation attaches to its review rather than showing twice),
 * then standalone escalated questions, then screening flags rolled up per
 * conversation. Pure and exported so AgentConsole's Review-queue tab badge can
 * count exactly what this page lists — a raw escalation-row count disagreed
 * with the visible list in both directions (dedup + the awaiting_review rows
 * a badge built from /agent/escalations alone can never see).
 */
export function buildQueueItems(escalations: QueueEscalation[], reviews: QueueReview[]): QueueItem[] {
  const out: QueueItem[] = [];
  const eligByConvo = new Map<string, QueueEscalation>();
  for (const e of escalations) {
    if (ELIG_KINDS.includes(e.kind)) {
      const prev = eligByConvo.get(e.conversation_id);
      if (!prev || e.created_at > prev.created_at) eligByConvo.set(e.conversation_id, e);
    }
  }
  const reviewConvos = new Set<string>();
  for (const r of reviews) {
    reviewConvos.add(r.conversation_id);
    const esc = eligByConvo.get(r.conversation_id) || null;
    out.push({
      key: `review-${r.conversation_id}`,
      group: 'eligibility',
      conversation_id: r.conversation_id,
      contact_id: r.contact_id,
      contact_name: r.contact_name,
      owner_user_id: r.owner_user_id ?? null,
      study_id: r.study_id,
      study_title: r.study_title,
      escalation: esc,
      created_at: esc?.created_at || r.created_at,
      claimed: esc?.status === 'claimed',
    });
  }
  for (const e of escalations) {
    if (SCREEN_KINDS.includes(e.kind)) continue; // grouped below
    const isElig = ELIG_KINDS.includes(e.kind);
    if (isElig && reviewConvos.has(e.conversation_id)) continue; // merged above
    if (!isElig && !QUESTION_KINDS.includes(e.kind)) continue;
    out.push({
      key: `esc-${e.id}`,
      group: isElig ? 'eligibility' : 'question',
      conversation_id: e.conversation_id,
      contact_id: e.contact_id || null,
      contact_name: e.contact_name || null,
      owner_user_id: e.owner_user_id ?? null,
      study_id: null,
      study_title: null,
      escalation: e,
      created_at: e.created_at,
      claimed: e.status === 'claimed',
    });
  }
  // Screening flags: ONE item per conversation carrying every open flag — a
  // lead with three concerning answers is one decision, not three rows.
  // Skipped when the conversation already has an eligibility item (the flags
  // roll up into that review's summary).
  const flagsByConvo = new Map<string, QueueEscalation[]>();
  for (const e of escalations) {
    if (!SCREEN_KINDS.includes(e.kind)) continue;
    if (reviewConvos.has(e.conversation_id)) continue;
    const list = flagsByConvo.get(e.conversation_id) || [];
    list.push(e);
    flagsByConvo.set(e.conversation_id, list);
  }
  for (const [convoId, flags] of flagsByConvo) {
    const newest = flags.reduce((a, b) => (a.created_at > b.created_at ? a : b));
    out.push({
      key: `screen-${convoId}`,
      group: 'screening',
      conversation_id: convoId,
      contact_id: newest.contact_id || null,
      contact_name: newest.contact_name || null,
      owner_user_id: newest.owner_user_id ?? null,
      study_id: null,
      study_title: null,
      escalation: newest,
      flags: flags.slice().sort((a, b) => a.created_at.localeCompare(b.created_at)),
      created_at: flags.reduce((a, b) => (a.created_at < b.created_at ? a : b)).created_at,
      claimed: flags.some((f) => f.status === 'claimed'),
    });
  }
  return out.sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export default function AgentReviewQueue({
  refreshTick, studies, onOpenConversation, onError,
}: {
  refreshTick: number;
  studies: StudyOption[];
  onOpenConversation: (conversationId: string) => void;
  onError: (msg: string) => void;
}) {
  const [escalations, setEscalations] = useState<QueueEscalation[]>([]);
  const [reviews, setReviews] = useState<QueueReview[]>([]);
  const [determinations, setDeterminations] = useState<QueueDetermination[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await api.get<{ escalations: QueueEscalation[]; reviews: QueueReview[]; determinations: QueueDetermination[] }>('/api/crm/agent/queue');
      setEscalations(res.data?.escalations || []);
      setReviews(res.data?.reviews || []);
      setDeterminations(res.data?.determinations || []);
    } catch (err) {
      console.error('[AgentReviewQueue] fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue, refreshTick]);

  // Same memo inputs/outputs as before — the grouping logic itself now lives
  // in buildQueueItems (shared with AgentConsole's Review-queue tab badge).
  const items = useMemo<QueueItem[]>(() => buildQueueItems(escalations, reviews), [escalations, reviews]);

  // Deep link from the Today list (?review=<conversation_id>): preselect that
  // conversation's item once, then let the user drive. The param is read-only
  // here — the console owns the URL.
  const [params] = useSearchParams();
  const reviewParam = params.get('review');
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (deepLinked || !reviewParam || !items.length) return;
    const match = items.find((i) => i.conversation_id === reviewParam);
    if (match) setSelectedKey(match.key);
    setDeepLinked(true);
  }, [deepLinked, reviewParam, items]);

  const selected = items.find((i) => i.key === selectedKey) || null;
  // Auto-select the oldest item so the pane never opens empty. Holds off while
  // an unconsumed ?review= deep link exists — both effects fire in the same
  // commit and the last setState would win, eating the deep link.
  useEffect(() => {
    if (!selected && items.length && (deepLinked || !reviewParam)) setSelectedKey(items[0].key);
  }, [selected, items, deepLinked, reviewParam]);

  const eligibility = items.filter((i) => i.group === 'eligibility');
  const screening = items.filter((i) => i.group === 'screening');
  const questions = items.filter((i) => i.group === 'question');
  const inFlight = determinations.filter((d) => d.status === 'submitted' || d.status === 'delivering');
  const recent = determinations.filter((d) => d.status !== 'submitted' && d.status !== 'delivering').slice(0, 12);

  const itemRow = (i: QueueItem) => (
    <button
      key={i.key}
      type="button"
      onClick={() => setSelectedKey(i.key)}
      className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors duration-150 ${selectedKey === i.key
        ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
        : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60'}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{i.contact_name || 'Unknown lead'}</span>
          {i.claimed && (
            <span className="rounded bg-blue-100 px-1 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">claimed</span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="truncate">
            {i.group === 'screening'
              ? `${i.flags?.length || 1} flagged answer${(i.flags?.length || 1) === 1 ? '' : 's'} · screening ongoing`
              : i.escalation ? (KIND_LABEL[i.escalation.kind] || i.escalation.kind) : 'Eligibility review'}
            {i.study_title ? ` · ${i.study_title}` : ''}
          </span>
        </div>
      </div>
      <span className="flex-shrink-0 text-[10px] text-gray-400">{ago(i.created_at)}</span>
      <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600" />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  }

  return (
    <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[340px_minmax(0,1fr)]">
      {/* Left: the queue */}
      <div className="space-y-3">
        <section>
          <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <ClipboardCheck className="h-3.5 w-3.5" /> Eligibility reviews
            <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">{eligibility.length}</span>
          </h3>
          {eligibility.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 px-3 py-3 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
              No leads waiting on a determination.
            </p>
          ) : <div className="space-y-1.5">{eligibility.map(itemRow)}</div>}
        </section>

        {screening.length > 0 && (
          <section>
            <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <XCircle className="h-3.5 w-3.5" /> Screening flags
              <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">{screening.length}</span>
            </h3>
            <p className="mb-1.5 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
              An answer looks disqualifying mid-screen. The agent keeps the conversation going — you can end it early with a determination, or leave it to finish.
            </p>
            <div className="space-y-1.5">{screening.map(itemRow)}</div>
          </section>
        )}

        <section>
          <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <HelpCircle className="h-3.5 w-3.5" /> Escalated questions
            <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">{questions.length}</span>
          </h3>
          {questions.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 px-3 py-3 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
              Nothing escalated right now.
            </p>
          ) : <div className="space-y-1.5">{questions.map(itemRow)}</div>}
        </section>

        {(inFlight.length > 0 || recent.length > 0) && (
          <section>
            <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <Bot className="h-3.5 w-3.5" /> Determinations
            </h3>
            <div className="space-y-1.5">
              {[...inFlight, ...recent].map((d) => (
                <div key={d.id} className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-900 dark:text-gray-100">{d.contact_name || 'Lead'}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${DET_STATUS_PILL[d.status] || DET_STATUS_PILL.canceled}`}>
                      {d.status === 'delivering' ? 'sending…' : d.status}
                    </span>
                    <span className="text-[10px] text-gray-400">{ago(d.created_at)}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                    {OUTCOME_LABEL[d.outcome] || d.outcome}
                    {d.status === 'failed' && d.error ? <span className="text-red-600 dark:text-red-400"> — {d.error}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Right: context + the determination composer */}
      <div>
        {!selected ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
            <ClipboardCheck className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">All caught up</p>
            <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">
              Escalated questions and leads needing an eligibility call land here. Your write-up is what the agent delivers, in a friendly way, and the outcome you pick is applied for you.
            </p>
          </div>
        ) : (
          <DeterminationWorkspace
            key={selected.key}
            item={selected}
            studies={studies}
            onOpenConversation={onOpenConversation}
            onError={onError}
            onSubmitted={fetchQueue}
          />
        )}
      </div>
    </div>
  );
}

export function DeterminationWorkspace({
  item, studies, onOpenConversation, onError, onSubmitted,
}: {
  item: QueueItem;
  studies: StudyOption[];
  onOpenConversation: (conversationId: string) => void;
  onError: (msg: string) => void;
  onSubmitted: () => void;
}) {
  const e = item.escalation;

  // ONE conversation-detail read feeds both the answers panel below and the
  // flag rows above it: a flag stored before the answer-label fix carries only
  // "option_4", and the live screen knows the words that value stands for.
  const { screen, loading: screenLoading, answerLabels } = useConversationScreen(item.conversation_id);

  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(false);

  /** "I already handled this outside the queue" — clears the item WITHOUT the
   *  agent texting the lead. Resolves whatever escalation(s) put it here; an
   *  eligibility item also hands the conversation off (there's no other way
   *  to clear awaiting_review short of a determination) — the agent won't
   *  auto-reply to this lead again. No confirmation step (owner ask
   *  2026-08-17): Resolve acts immediately on click, every item type. */
  const resolveOutsideQueue = async () => {
    if (resolved || resolving) return;
    setResolving(true);
    try {
      const swallowConflict = (p: Promise<any>) => p.catch((err: any) => {
        if (err?.response?.status !== 409) throw err;
      });
      const flagIds = item.group === 'screening' && item.flags?.length
        ? item.flags.map((f) => f.id)
        : e ? [e.id] : [];
      await Promise.all(flagIds.map((id) => swallowConflict(
        api.post(`/api/crm/agent/escalations/${id}/resolve`, { note: 'resolved_outside_queue' }),
      )));
      if (item.group === 'eligibility') {
        await swallowConflict(api.post(`/api/crm/agent/conversations/${item.conversation_id}/takeover`));
      }
      setResolved(true);
      onSubmitted();
    } catch (err: any) {
      onError(`Resolve failed: ${err?.response?.data?.error || err?.message || 'request failed'}`);
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      {/* Who + where */}
      <div className="flex flex-wrap items-center gap-2">
        <User className="h-4 w-4 text-gray-400" />
        {item.contact_id ? (
          <RowLink to={`/crm/contacts/${item.contact_id}`} className="text-sm font-semibold text-gray-900 hover:underline dark:text-gray-100">
            {item.contact_name || 'Unknown lead'}
          </RowLink>
        ) : (
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.contact_name || 'Unknown lead'}</span>
        )}
        {item.study_title && <span className="text-xs text-gray-500 dark:text-gray-400">· {item.study_title}</span>}
        <RecruiterBadge ownerId={item.owner_user_id} />
        <span className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onOpenConversation(item.conversation_id)}
            className={btnGhost}
          >
            <ExternalLink className="h-3.5 w-3.5" /> Open thread
          </button>
          {!resolved && (
            <button
              type="button"
              onClick={resolveOutsideQueue}
              disabled={resolving}
              title={item.group === 'eligibility'
                ? "Already handled — clears this and hands the conversation off. Does not text the lead."
                : 'Already handled — clears this without texting the lead.'}
              className={btnGhost}
            >
              {resolving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Resolve
            </button>
          )}
        </span>
      </div>

      {/* What needs deciding */}
      {item.group === 'screening' && item.flags?.length ? (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5 dark:border-amber-800 dark:bg-amber-900/10">
          <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
            {item.flags.length} flagged answer{item.flags.length === 1 ? '' : 's'} — screening is still going
          </div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
            The lead has NOT been told anything. The agent keeps the conversation going until you act. "Resolve flag" clears this with no change; any other determination ends the screen early.
          </p>
          {item.flags.map((f) => (
            <EscalationBreakdown
              key={f.id}
              e={f}
              answerLabel={f.payload?.question_key ? answerLabels.get(String(f.payload.question_key)) || null : null}
            />
          ))}
          <EscalationContext escalationId={(e || item.flags[item.flags.length - 1]).id} />
        </div>
      ) : e ? (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2.5 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">{KIND_LABEL[e.kind] || e.kind}</div>
          {escalationGuide(e) && <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">{escalationGuide(e)}</p>}
          <EscalationBreakdown
            e={e}
            answerLabel={e.payload?.question_key ? answerLabels.get(String(e.payload.question_key)) || null : null}
          />
          <EscalationContext escalationId={e.id} />
        </div>
      ) : null}

      {/* Owner ask 2026-08-04: the screening answers themselves, right here —
          a determination should never require leaving the queue. */}
      {(item.group === 'eligibility' || item.group === 'screening') && (
        <ScreenAnswersPanel screen={screen} loading={screenLoading} />
      )}

      {resolved ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            Cleared — no message was sent to the lead.
            {item.group === 'eligibility' && ' The conversation is handed off; the agent will not reply here automatically anymore.'}
          </div>
        </div>
      ) : (
        <DeterminationComposer
          conversationId={item.conversation_id}
          group={item.group}
          currentStudyId={item.study_id}
          escalationId={e?.id || null}
          studies={studies}
          defaultOutcome={item.group === 'question' ? 'info' : ''}
          onSubmitted={onSubmitted}
          onError={onError}
        />
      )}

      {!resolved && item.group !== 'question' && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
          <XCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
          The agent only ever shares what YOU write: reasons reach the lead solely through the reason box, softened and scoped to this study — never medical detail, never "failed" or "disqualified".
        </p>
      )}
    </div>
  );
}
